import to from 'await-catch'
import React, { useState } from 'react'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { Calendar } from '../../../assets/icons'
import AddToCalendarQuickAccessModal from '../../../components/addToCalendarQuickAccessModal'
import { Button } from '../../../components/ui/button'
import { posConfirmAppointment } from '../../../api'
import useIsMobile from '../../../hooks/useIsMobile'
import theme from '../../../styles/theme'
import { buildAppointmentCalendarEvent } from '../../../utils/appointmentCalendarEvent'
import { buildConfirmAppointmentParams } from './buildConfirmAppointmentParams'
import { BackTitle } from '../selectAppointment/SelectAppointment'
import { ChevronLeft } from '../../../assets/icons'
import { parseProfessionalName } from '../../../utils/stringsUtils'
import moment from 'moment'

const ConfirmSingleAppointment = ({
  onBack,
  onConfirmed,
  turn,
  patient,
  clinics,
  showLoader,
  hideLoader,
  addFlashMessage,
  auth,
  onBackInit
}) => {
  const history = useHistory()
  const isMobile = useIsMobile()
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [showCalendarModal, setShowCalendarModal] = useState(false)

  const clinicName =
    clinics?.find(clinic => String(clinic.codigoEmpresa) === String(turn?.clinica))?.nombre ||
    turn?.direccion

  const calendarEvent =
    isConfirmed && turn ? buildAppointmentCalendarEvent(turn, { clinicName }) : null

  const formattedAppointmentDate =
    turn && turn.fechaTurnoDateTime
      ? `${moment(turn.fechaTurnoDateTime).format('D [de]')} ${moment(turn.fechaTurnoDateTime)
          .format('MMMM')
          .replace(/^./, letter => letter.toUpperCase())} ${moment(turn.fechaTurnoDateTime).format(
          'YYYY [a las] hh:mm A'
        )}`
      : turn?.fechaTurno

  const confirm = async () => {
    if (!turn) {
      addFlashMessage('No hay turno seleccionado para confirmar')
      return
    }

    try {
      showLoader()
      const params = buildConfirmAppointmentParams({ turn, patient, auth })
      const firebaseToken = localStorage.getItem('fcmToken') || ''
      const [err] = await to(posConfirmAppointment(params, firebaseToken))

      if (err) throw new Error('Error del servidor')

      setIsConfirmed(true)
      if (onConfirmed) onConfirmed()
    } catch (error) {
      addFlashMessage('Ocurrió un error al confirmar el turno')
    } finally {
      hideLoader()
    }
  }

  return (
    <>
      <AddToCalendarQuickAccessModal
        isOpen={showCalendarModal}
        onClose={() => setShowCalendarModal(false)}
        isFullHeight={isMobile}
        event={calendarEvent}
      />
      <Container>
        <Content>
          {!isConfirmed && !isMobile && (
            <BackTitle m={'0'} onClick={onBack}>
              <ChevronLeft size={8} /> Volver
            </BackTitle>
          )}

          <Title>{isConfirmed ? 'Turno confirmado' : 'Agendar turno'}</Title>

          <Section>
            <SectionTitle>Detalle</SectionTitle>
            <DetailsGrid>
              <DetailItem>
                <DetailLabel>Profesional</DetailLabel>
                <DetailValue>{turn?.nombreProfesional ? parseProfessionalName(turn.nombreProfesional) : '-'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Especialidad</DetailLabel>
                <DetailValue>{turn?.nombreEspecialidad || '-'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Fecha</DetailLabel>
                <DetailValue>{formattedAppointmentDate || '-'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Dirección</DetailLabel>
                <DetailValue>{turn?.direccion || '-'}</DetailValue>
              </DetailItem>
            </DetailsGrid>
          </Section>
        </Content>

        <ButtonWrapper>
          {!isConfirmed ? (
            <Button
              type="button"
              onClick={confirm}
              label="Confirmar"
              size={isMobile ? 'mobile' : 'xlarge'}
            />
          ) : (
            <>
              <Button
                $showBorder={false}
                $mt={'0px'}
                label={'Volver al inicio'}
                $variant="primary"
                size={isMobile ? 'mobile' : 'xlarge'}
                onClick={() => (onBackInit ? onBackInit() : history.push('/inicio'))}
              />
              <Button
                iconLeft={<Calendar color={theme.colors.brand.blueOmint} />}
                $showBorder={false}
                $mt={'0px'}
                label={'Agregar a calendario'}
                $variant="secondary"
                size={isMobile ? 'mobile' : 'xlarge'}
                $underline={true}
                onClick={() => setShowCalendarModal(true)}
              />
            </>
          )}
        </ButtonWrapper>
      </Container>
    </>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 80vh;
  width: 80vw;
  padding: 20px;

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    width: 100%;
    padding: 16px;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 16px;
`

const Section = styled.div`
  margin-bottom: 24px;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  color: ${props => props.theme.colors.text.titleText};

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    font-size: 24px;
  }
`

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.text.titleText};
  margin: 0 0 16px 0;
`

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    gap: 16px;
  }
`

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DetailLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.colors.text.titleText};
`

const DetailValue = styled.p`
  font-size: 16px;
  color: ${props => props.theme.colors.text.secondaryText};
  margin: 0;
  font-weight: 400;
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 360px;

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    margin-top: 16px;
    width: 100%;
  }
`

export default ConfirmSingleAppointment
